/**
 * A project quote, composed — `mockups/studio/client-detail.html`'s topbar
 * **New work order**.
 *
 * `.docs/20-studio-portal-pages.md` §DRY watchlist: "`PackageBuilder` and
 * `QuoteBuilder` are the only price-composition UIs; both call
 * `catalog_compute_price` and render the breakdown through one `Breakdown`
 * component." This is the project half of that sentence and `PackageBuilder` is the
 * monthly half — same shape, same debounce, same "nothing to price yet" branch, and
 * the same rule under both: **nothing here computes a price.** Every figure comes
 * back from `catalog_compute_price`, which is the engine `work_orders_price` prices
 * with, so the number on the confirm button is the number on the quote.
 *
 * ## Three calls, in order, and all three **after** the confirmation
 *
 * A quote is `work_orders_draft` → `work_orders_price` → `work_orders_propose$`
 * (§C's own table). The first two are `delivery` actions that commit nothing: a draft
 * with a price on it is a proposal nobody has seen. The third is the `$` one — it
 * sends the client a decision and starts a 30-day quote.
 *
 * **Session 24 (R2-18, R2-19) moved all three behind the confirmation.** They used to
 * run in front of it, on the argument that a confirmation has to name the amount *and
 * the reference* and neither exists until the work order does. The amount half of that
 * was never true: `catalog_compute_price` is the engine `work_orders_price` prices
 * with, so the number is on screen before anything is written. The reference half was
 * true and was not worth what it cost — pressing *Price it and send* created
 * WO-2026-023 at $1,080.00 and *then* asked, and "Keep it as a draft" left a priced,
 * visible work order in the ledger with no control on its row, in a dialog whose only
 * commit still read "Price it and send" and whose only exit was the ✕. A dead end
 * three clicks from the front page.
 *
 * So: the confirmation names the **amount and the client**, Cancel writes nothing at
 * all, and the reference is what the reader is taken to afterwards — the work order's
 * own page, which is a stronger confirmation than a sentence.
 *
 * **A partial failure still leaves a draft, and now says so usefully.** If `draft` and
 * `price` land and `propose` does not, the row exists; the reference is kept and the
 * button becomes *Send WO-2026-023*, so a retry re-proposes the row rather than
 * raising a second one. That is the honest remainder of the old trade, and it is a
 * state somebody reached by a failure rather than by pressing Cancel.
 *
 * ## What it will not offer
 *
 * Only `fixed` and `percent` add-ons reach the engine (`ADD_ON_PRICING`'s own
 * comment). A band, a floor and a "quote" are on the rate card because
 * `client/billing.html` prints them, and offering one here would produce a refusal by
 * name instead of a price. They are named in a muted line, so the reader knows they
 * exist and knows to quote them by hand.
 */

import { useMemo, useState } from 'react'
import { useFieldArray } from 'react-hook-form'

import {
  MoneySchema,
  isNeedsReview,
  type ComputedPrice,
  type Currency,
  type Money,
  type NeedsReviewResult,
  type RateCardAddOn,
  type RateCardTemplate,
  type TierCode,
} from '@fareast/shared'
import { z } from 'zod'

import { messageOf } from '../../api/problem.js'
import { useStudioAction, useStudioRead } from '../../api/studio.js'
import { useFormat } from '../../format/useFormat.js'
import { Button, ButtonRow } from '../portal/Button.js'
import { Empty } from '../portal/Disclosure.js'
import {
  Input,
  Select,
  Switch,
  Textarea,
  useZodForm,
  type SelectChoice,
  type SelectOption,
} from '../portal/Form.js'
import { Row, ScrollX, Stack } from '../portal/Layout.js'
import { ConfirmDialog } from '../portal/Overlay.js'
import { Notice } from '../portal/Status.js'
import { TBody, THead, Table, Td, Th, Tr } from '../portal/Table.js'
import { HeroCap, Muted, Num, Prose, Ref, Small, SubFigure } from '../portal/Text.js'
import { commitSentence, confirmLabel } from './ActionForm.js'
import { DEPOSIT_ON_APPROVAL } from './actionCopy.js'
import { Breakdown } from './Breakdown.js'
import { useDebounced } from './useDebounced.js'
import { NeedsReviewNotice } from './NeedsReviewNotice.js'

/** How long to wait after the last change before pricing. `PackageBuilder`'s number. */

/** The two an engine can put a number on — `ADD_ON_PRICING`'s own first two. */
const PRICEABLE: readonly RateCardAddOn['pricing'][] = ['fixed', 'percent']

/** The schedule `work_orders.propose` writes when a caller sends none (session 11). */
export const DEFAULT_MILESTONES: readonly MilestoneValue[] = [
  { title: 'On approval', percent: '50' },
  { title: 'On delivery', percent: '50' },
]

/**
 * A milestone, as the form holds it.
 *
 * `percent` is a string because that is what a `number` box gives back and because
 * `useZodForm` binds the schema's **input** shape: a coerced number would make the
 * field type `unknown` and every control on the form untypeable. It is turned into
 * an integer once, at the send.
 */
export type MilestoneValue = {
  readonly title: string
  readonly percent: string
}

export const QuoteFormSchema = z.object({
  template: z.string().min(1, 'Choose what kind of project this is.'),
  title: z.string().min(3, 'One line, as it will read in the ledger.'),
  scope: z.string(),
  deposit_on_approval: z.boolean(),
  milestones: z
    .array(
      z.object({
        title: z.string().min(3, 'Give the milestone a name.'),
        percent: z.string().regex(/^\d{1,3}$/u, 'A whole percentage, 1 to 100.'),
      }),
    )
    .min(1, 'A project has at least one milestone.')
    .max(12, 'Twelve milestones is the most a schedule may carry.'),
})

export type QuoteFormValues = z.infer<typeof QuoteFormSchema>

export type QuoteBuilderProps = {
  /** The client the quote is for, by slug. */
  readonly client: string
  /** Every project template on the card — `catalog_list_project_templates`. */
  readonly templates: readonly RateCardTemplate[]
  readonly tier: TierCode
  readonly currency: Currency
  /**
   * True when this client is on a live retainer.
   *
   * `PRICING.md` §Projects takes 10% off a project signed alongside one, and
   * `work_orders.price` reads that from the client's own row rather than from the
   * request — so the preview has to be told the same thing the engine will decide
   * for itself, or the number on screen is 10% away from the number on the quote.
   */
  readonly retainerClient: boolean
  /** Run once the quote has gone, with the reference it went as. */
  readonly onDone?: ((ref: string) => void) | undefined
}

/**
 * What the two `delivery` steps answer with, narrowed to the fields this reads —
 * local schemas rather than `WorkOrderDetailSchema`, because the reference and the
 * total are the whole of what a quote builder needs back, and parsing the comment
 * thread and the attachment list to get at them would make an unrelated field's
 * rename look like a broken button.
 *
 * Two schemas, not one, because `total_amount` means something different on each
 * answer. `work_orders_draft` answers before anything has been priced, and
 * `WorkOrderSchema.total_amount` is `null` there by design — "or null while it is
 * still to be quoted", never a zero. `work_orders_price` requires a template or a
 * list of units to succeed at all (`work_orders.price`, `.docs/11-work-orders.md`),
 * so its own answer always carries a real total. Parsing the *draft* answer against a
 * schema that demands the priced shape is exactly the bug this split exists to
 * prevent — a fresh draft's legitimate `null` read as a broken response.
 */
const DraftedSchema = z.object({
  work_order: z.object({ ref: z.string() }),
})

const PricedSchema = z.object({
  work_order: z.object({ ref: z.string(), total_amount: MoneySchema }),
})

type Drafted = { readonly ref: string; readonly amount: Money }

export function QuoteBuilder({
  client,
  templates,
  tier,
  currency,
  retainerClient,
  onDone,
}: QuoteBuilderProps) {
  const format = useFormat()

  const form = useZodForm<QuoteFormValues, QuoteFormValues>(QuoteFormSchema, {
    defaultValues: {
      template: '',
      title: '',
      scope: '',
      deposit_on_approval: true,
      milestones: [...DEFAULT_MILESTONES],
    },
  })
  const schedule = useFieldArray({ control: form.control, name: 'milestones' })

  const templateCode = form.watch('template')
  const title = form.watch('title')
  const milestones = form.watch('milestones')

  const [quantities, setQuantities] = useState<Readonly<Record<string, number>>>({})
  /*
   * The row this builder has already created, when it has created one.
   *
   * Null until the reader confirms, and null again once the quote has gone. It is
   * only ever non-null in one situation: `draft` and `price` landed and `propose`
   * did not, which leaves a real work order that a retry must re-propose rather than
   * duplicate. It is not "the dialog is open" — Cancel writes nothing now, so there
   * is nothing to remember.
   */
  const [drafted, setDrafted] = useState<Drafted | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const [review, setReview] = useState<NeedsReviewResult | null>(null)

  const draft = useStudioAction('work_orders_draft')
  const price = useStudioAction('work_orders_price')
  const propose = useStudioAction('work_orders_propose')

  /*
   * What each service is called, for the group headings. `inline: true` for the
   * reason `catalog_compute_price` below is: a picker that fell back to service codes
   * because one read was slow is a worse page than a boundary, not a better one.
   */
  const services = useStudioRead('catalog_list_services', {}, { inline: true })
  const serviceNames = useMemo(
    () => new Map((services.data?.services ?? []).map((one) => [one.code, one.name])),
    [services.data],
  )

  const choosable = useMemo(() => templates.filter((one) => one.active), [templates])
  const template = useMemo(
    () => choosable.find((one) => one.code === templateCode) ?? null,
    [choosable, templateCode],
  )

  const offered = useMemo(
    () => (template?.add_ons ?? []).filter((one) => one.active && PRICEABLE.includes(one.pricing)),
    [template],
  )
  const quoted = useMemo(
    () => (template?.add_ons ?? []).filter((one) => one.active && !PRICEABLE.includes(one.pricing)),
    [template],
  )

  /*
   * The add-ons as the engine wants them, in the order the **template** lists them:
   * `catalog_compute_price`'s own `add_ons` is "in the order they should read on the
   * quote", so the order somebody ticked them in is deliberately not the quote's.
   */
  const addOns = useMemo(
    () =>
      offered
        .filter((one) => (quantities[one.code] ?? 0) > 0)
        .map((one) => ({ code: one.code, qty: quantities[one.code] ?? 0 })),
    [offered, quantities],
  )

  const live = useMemo(
    () =>
      ({
        kind: 'project',
        template: templateCode,
        add_ons: addOns,
        retainer_client: retainerClient,
      }) as const,
    [templateCode, addOns, retainerClient],
  )
  const request = useDebounced(live)

  const computed = useStudioRead(
    'catalog_compute_price',
    { request, tier, currency },
    { enabled: request.template !== '', inline: true },
  )
  const priced: ComputedPrice | null = computed.data ?? null

  const percentTotal = milestones.reduce((sum, one) => sum + percentOf(one.percent), 0)
  const working = draft.isPending || price.isPending || propose.isPending

  /* ------------------------------------------------------------------- the run */

  /**
   * Draft, price and send — in that order, and only once somebody has confirmed.
   *
   * The first two commit nothing and the third is the `$` one, so the whole sequence
   * sits behind one confirmation rather than the confirmation sitting in the middle
   * of it. `drafted` is the resume point: a second press after a failed `propose`
   * skips straight to proposing the row that already exists.
   */
  const send = async (values: QuoteFormValues) => {
    setFailure(null)
    setReview(null)
    try {
      let row = drafted
      if (row === null) {
        const drafting = await draft.mutateAsync({
          kind: 'project',
          client,
          service: template?.service ?? '',
          title: values.title,
          scope: values.scope,
        })
        if (isNeedsReview(drafting)) {
          setReview(drafting)
          setConfirming(false)
          return
        }

        const pricing = await price.mutateAsync({
          work_order_ref: DraftedSchema.parse(drafting).work_order.ref,
          template: values.template,
          add_ons: addOns,
        })
        if (isNeedsReview(pricing)) {
          setReview(pricing)
          setConfirming(false)
          return
        }
        const priced = PricedSchema.parse(pricing).work_order
        row = { ref: priced.ref, amount: priced.total_amount }
        // Kept before the `$` call, not after it: if `propose` throws, this is the
        // row the retry has to re-propose rather than raise a second one.
        setDrafted(row)
      }

      const answer = await propose.mutateAsync({
        work_order_ref: row.ref,
        amount: row.amount,
        milestones: values.milestones.map((one) => ({
          title: one.title,
          percent: percentOf(one.percent),
        })),
        deposit_on_approval: values.deposit_on_approval,
      })
      const sent = row.ref
      setDrafted(null)
      setConfirming(false)
      if (isNeedsReview(answer)) {
        setReview(answer)
        return
      }
      onDone?.(sent)
    } catch (error: unknown) {
      setConfirming(false)
      setFailure(messageOf(error))
    }
  }

  /* ------------------------------------------------------------------ the form */

  return (
    <Stack>
      <form
        noValidate
        onSubmit={(event) => {
          /*
           * Submitting opens the confirmation and writes nothing. The resolver has
           * run by the time this is called, so a form that is missing a title or
           * whose shares do not come to 100 never reaches the dialog.
           */
          void form.handleSubmit(() => {
            setFailure(null)
            setConfirming(true)
          })(event)
        }}
      >
        <Stack>
          <Select
            control={form.control}
            name="template"
            label="What kind of project"
            hint="The template it is quoted from. Its own add-ons appear underneath."
            placeholder="Choose a template"
            options={templateChoices(choosable, serviceNames, format.money)}
          />

          <Input
            control={form.control}
            name="title"
            label="Title"
            hint="One line, as it will read in the ledger and on the invoice."
          />

          <Textarea
            control={form.control}
            name="scope"
            label="Scope"
            hint="What it covers, in the words the client will read on the quote."
          />

          {template === null ? (
            <Empty>Choose a template and it comes back priced at this client’s tier.</Empty>
          ) : (
            <>
              <AddOnPicker
                addOns={offered}
                quantities={quantities}
                onChange={(code, qty) => {
                  setQuantities((was) => ({ ...was, [code]: qty }))
                }}
              />

              {quoted.length === 0 ? null : (
                <Small>
                  <Muted>
                    {quoted.map((one) => one.name).join(', ')} {quoted.length === 1 ? 'is' : 'are'}{' '}
                    quoted per item and cannot be priced off the card. Raise{' '}
                    {quoted.length === 1 ? 'it' : 'them'} as a scope change once the project is
                    running.
                  </Muted>
                </Small>
              )}

              <MilestoneEditor
                control={form.control}
                fields={schedule.fields}
                total={percentTotal}
                onAppend={() => {
                  schedule.append({ title: 'On delivery', percent: '0' })
                }}
                onRemove={(index) => {
                  schedule.remove(index)
                }}
              />

              {/*
                The words are `actionCopy.ts`'s, not this file's. The same question is
                asked by the generic form on a work order's own page, and the two used
                to carry the same sentence twice — which is how they came to be wrong
                twice: the switch is **on** by default and the line under it described
                the off position.
              */}
              <Switch
                control={form.control}
                name="deposit_on_approval"
                label={DEPOSIT_ON_APPROVAL.label}
                hint={DEPOSIT_ON_APPROVAL.hint}
              />
            </>
          )}

          {computed.isError ? (
            <Notice need role="alert">
              We could not price that. Nothing has been sent — change the add-ons and try again.
            </Notice>
          ) : priced === null ? null : (
            <Stack gap="tight">
              <div>
                <HeroCap>What this quote comes to</HeroCap>
                <SubFigure value={priced.total} />
              </div>
              <Breakdown price={priced} templateName={template?.name} />
            </Stack>
          )}

          {review === null ? null : <NeedsReviewNotice review={review} />}
          {failure === null ? null : (
            <Notice need role="alert">
              {failure}
            </Notice>
          )}

          <ButtonRow>
            <Button
              variant="primary"
              type="submit"
              disabled={working || priced === null || percentTotal !== 100}
            >
              {working ? 'Working…' : drafted === null ? 'Send the quote' : `Send ${drafted.ref}`}
            </Button>
            {drafted === null ? null : (
              <Small>
                <Muted>
                  <Ref value={drafted.ref} /> was raised and priced, and sending it did not finish.
                  Sending again proposes that row rather than raising a second one.
                </Muted>
              </Small>
            )}
          </ButtonRow>
        </Stack>
      </form>

      {/*
        The confirmation names the **amount**, and the reference only once there is
        one — which, after R2-19, is only on a retry. `.docs/20` §Design decisions
        asks a `$` button to name the amount and the reference; a quote nobody has
        raised has no reference, and `confirmLabel` is the same builder `ActionForm`
        uses for every other action whose input names none.

        `cancelLabel` is "Not yet" rather than "Keep it as a draft", because there is
        no draft to keep: nothing has been written when this dialog is open for the
        first time.
      */}
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Send the quote to the client"
        confirmLabel={confirmLabel(
          'Send',
          priced === null ? null : format.money(priced.total),
          drafted?.ref ?? null,
        )}
        cancelLabel="Not yet"
        busy={working}
        onConfirm={() => {
          void send(form.getValues())
        }}
      >
        <Prose>
          {commitSentence(
            'Sending this quote',
            priced === null ? null : format.money(priced.total),
            drafted?.ref ?? null,
          )}{' '}
          {title === '' ? '' : `It is “${title}”. `}It is raised against {client} and sent to them
          the moment you confirm; they have 30 days to approve it, and approving it is what commits
          them. Nothing has been written yet.
        </Prose>
      </ConfirmDialog>
    </Stack>
  )
}

/** `"50"` → `50`; anything a `number` box cannot produce → `0`. */
function percentOf(raw: string): number {
  const value = Number(raw)
  return Number.isFinite(value) ? value : 0
}

/**
 * The templates, grouped by the service they belong to — R2-20.
 *
 * Eleven flat options with nothing to navigate by: `Foundation · $500.00` is Odoo
 * setup and `Discovery, fixed · $800.00` is custom software, and neither option said
 * so. The client's **own** `/app/billing` prints the identical list grouped by
 * service, and the two should not disagree about how the same eleven things are
 * organised.
 *
 * `SelectGroup` is session 24's own primitive, added for the 419-option timezone list
 * and reused here rather than a second answer to the same problem
 * (`components/portal/Form.tsx`). The groups come out in the order the **templates**
 * arrive in, which is the rate card's own — the first template of a service opens its
 * group — so the ordering is the API's rather than an alphabetisation this file
 * invented.
 *
 * A service whose name the card has not answered yet keeps its code as the group
 * label. That is a heading nobody has to guess at, and it beats waiting for a second
 * read before the picker can be drawn at all.
 */
function templateChoices(
  templates: readonly RateCardTemplate[],
  serviceNames: ReadonlyMap<string, string>,
  money: (amount: Money) => string,
): readonly SelectChoice[] {
  const groups = new Map<string, { label: string; options: SelectOption[] }>()
  for (const template of templates) {
    const group = groups.get(template.service) ?? {
      label: serviceNames.get(template.service) ?? template.service,
      options: [],
    }
    group.options.push({
      value: template.code,
      label: `${template.name} · ${money(template.base_amount)}`,
    })
    groups.set(template.service, group)
  }
  return [...groups.values()]
}

/* ------------------------------------------------------------------- the add-ons */

function AddOnPicker({
  addOns,
  quantities,
  onChange,
}: {
  readonly addOns: readonly RateCardAddOn[]
  readonly quantities: Readonly<Record<string, number>>
  readonly onChange: (code: string, qty: number) => void
}) {
  const format = useFormat()
  if (addOns.length === 0) return null

  return (
    <ScrollX>
      <Table aria-label="Add-ons on this template">
        <THead>
          <Tr>
            <Th scope="col">Add-on</Th>
            <Th scope="col" right>
              Each
            </Th>
            <Th scope="col" right>
              How many
            </Th>
          </Tr>
        </THead>
        <TBody>
          {addOns.map((one) => {
            const qty = quantities[one.code] ?? 0
            /*
             * A percentage add-on is a share of the template and is not bought twice
             * — `catalog_compute_price`'s own `qty` is "always 1 for a percentage
             * add-on". The ceiling is what stops the form sending a body the engine
             * would refuse.
             */
            const ceiling = one.per_unit ? 100 : 1
            return (
              <Tr key={one.code} variant={qty > 0 ? undefined : 'sub'}>
                <Td>
                  {one.name}
                  {one.note === null ? null : (
                    <Small>
                      <Muted>{one.note}</Muted>
                    </Small>
                  )}
                </Td>
                <Td right num>
                  {one.amount === null ? (
                    <Muted>
                      {one.percent_of_template_bps === null
                        ? '—'
                        : `+${format.percent(one.percent_of_template_bps)}`}
                    </Muted>
                  ) : (
                    <Num value={one.amount} />
                  )}
                </Td>
                <Td right>
                  <Row>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={qty === 0}
                      onClick={() => {
                        onChange(one.code, qty - 1)
                      }}
                    >
                      <span aria-label={`One fewer ${one.name}`}>−</span>
                    </Button>
                    <Num value={qty} />
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={qty >= ceiling}
                      onClick={() => {
                        onChange(one.code, qty + 1)
                      }}
                    >
                      <span aria-label={`One more ${one.name}`}>+</span>
                    </Button>
                  </Row>
                </Td>
              </Tr>
            )
          })}
        </TBody>
      </Table>
    </ScrollX>
  )
}

/* ----------------------------------------------------------------- the schedule */

/**
 * The milestone percents — `work_orders_propose`'s own `milestones[]`.
 *
 * The shares sum to 100 and the API refuses a schedule that does not, so the running
 * total is printed and the send button is disabled rather than the reader finding out
 * from a 400. **Nothing here turns a percentage into money**: the amounts belong to
 * the invoices the schedule will produce, and printing them would be this builder
 * computing a price.
 */
function MilestoneEditor({
  control,
  fields,
  total,
  onAppend,
  onRemove,
}: {
  readonly control: ReturnType<typeof useZodForm<QuoteFormValues, QuoteFormValues>>['control']
  readonly fields: readonly { readonly id: string }[]
  readonly total: number
  readonly onAppend: () => void
  readonly onRemove: (index: number) => void
}) {
  const format = useFormat()

  return (
    <Stack gap="tight">
      {fields.map((row, index) => (
        <Row key={row.id} top>
          <Input
            control={control}
            name={milestonePath(index, 'title')}
            label={`Milestone ${format.number(index + 1)}`}
          />
          <Input
            control={control}
            name={milestonePath(index, 'percent')}
            type="number"
            label="Share, %"
          />
          <Button
            size="sm"
            variant="ghost"
            disabled={fields.length === 1}
            onClick={() => {
              onRemove(index)
            }}
          >
            Remove
          </Button>
        </Row>
      ))}

      <Row>
        <Button size="sm" disabled={fields.length >= 12} onClick={onAppend}>
          Add a milestone
        </Button>
        <Small>
          <Muted>
            The shares come to {format.number(total)}%
            {total === 100 ? '.' : ' — a schedule has to come to 100%.'}
          </Muted>
        </Small>
      </Row>
    </Stack>
  )
}

/**
 * `milestones.3.percent`, as react-hook-form's own field path.
 *
 * The cast is the whole of it, and it is here rather than at two call sites:
 * `FieldPath` spells an array index as `${number}` and
 * `@typescript-eslint/restrict-template-expressions` refuses a number inside a
 * template literal, so the string is built from `String(index)` and then told what it
 * is. There is no way to name an array field without one.
 */
function milestonePath<F extends 'title' | 'percent'>(
  index: number,
  field: F,
): `milestones.${number}.${F}` {
  return `milestones.${String(index)}.${field}` as `milestones.${number}.${F}`
}

/* ------------------------------------------------------------------ the debounce */
